<template>
  <transition name="bottom-sheet">
    <div v-if="value" class="bottom-sheet-backdrop" @click.self="close">
      <div
        ref="panel"
        class="bottom-sheet__panel"
        :class="{ 'is-dragging': dragging }"
        :style="panelStyle"
        role="dialog"
        aria-modal="true"
        :aria-label="title || null"
        tabindex="-1"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp">
        <div class="bottom-sheet__handle" aria-hidden="true"></div>
        <div v-if="title" class="bottom-sheet__header">
          <h2 class="bottom-sheet__title">{{ title }}</h2>
        </div>
        <div class="bottom-sheet__body">
          <slot />
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
const FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), '
  + 'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const DRAG_CLOSE_THRESHOLD = 60;

export default {
  name: 'BottomSheet',
  props: {
    value: { type: Boolean, default: false },
    title: { type: String, default: '' },
  },
  data() {
    return {
      dragging: false,
      dragStartY: 0,
      dragY: 0,
      opener: null,
      previousBodyOverflow: '',
    };
  },
  computed: {
    panelStyle() {
      return this.dragging ? { transform: `translateY(${this.dragY}px)` } : null;
    },
  },
  watch: {
    value(open) {
      if (open) this.onOpen();
      else this.onClose();
    },
  },
  beforeDestroy() {
    if (this.value) this.onClose();
  },
  methods: {
    onOpen() {
      this.opener = document.activeElement;
      this.previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this.onKeydown);
      this.$nextTick(() => {
        if (this.$refs.panel) this.$refs.panel.focus();
      });
    },
    onClose() {
      document.body.style.overflow = this.previousBodyOverflow;
      document.removeEventListener('keydown', this.onKeydown);
      this.dragging = false;
      this.dragY = 0;
      if (this.opener && this.opener.focus) this.opener.focus();
      this.opener = null;
    },
    close() {
      this.$emit('input', false);
    },
    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key === 'Tab') this.trapFocus(event);
    },
    trapFocus(event) {
      const panel = this.$refs.panel;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR));
      if (!focusable.length) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const atFirst = document.activeElement === first || document.activeElement === panel;
      if (event.shiftKey && atFirst) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    // drag cuma dari handle/header
    onPointerDown(event) {
      if (!event.target.closest('.bottom-sheet__handle, .bottom-sheet__header')) return;
      this.dragging = true;
      this.dragStartY = event.clientY;
      if (event.target.setPointerCapture) event.target.setPointerCapture(event.pointerId);
    },
    onPointerMove(event) {
      if (!this.dragging) return;
      this.dragY = Math.max(0, event.clientY - this.dragStartY);
    },
    onPointerUp(event) {
      if (!this.dragging) return;
      this.dragging = false;
      const shouldClose = this.dragY > DRAG_CLOSE_THRESHOLD;
      this.dragY = 0;
      if (event.target.releasePointerCapture) event.target.releasePointerCapture(event.pointerId);
      if (shouldClose) this.close();
    },
  },
};
</script>

<style scoped>
.bottom-sheet-backdrop {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background-color: var(--mz-overlay);
}

.bottom-sheet__panel {
  width: 100%;
  max-width: 480px;
  max-height: min(80vh, 640px);
  overflow-y: auto;
  background-color: var(--mz-surface);
  border-radius: var(--mz-radius-lg) var(--mz-radius-lg) 0 0;
  box-shadow: var(--mz-shadow-2);
  padding-bottom: calc(var(--mz-space-4) + env(safe-area-inset-bottom));
}

.bottom-sheet__panel:focus {
  outline: none;
}

.bottom-sheet__panel.is-dragging {
  transition: none;
}

.bottom-sheet__handle {
  width: 36px;
  height: 4px;
  margin: var(--mz-space-2) auto;
  border-radius: var(--mz-radius-pill);
  background-color: var(--mz-border-strong);
}

.bottom-sheet__header {
  padding: 0 var(--mz-space-4) var(--mz-space-2);
}

.bottom-sheet__title {
  margin: 0;
  font-size: var(--mz-fs-lg);
  font-weight: var(--mz-fw-title);
  color: var(--mz-text-strong);
}

.bottom-sheet__body {
  padding: 0 0 var(--mz-space-2);
}

.bottom-sheet-enter-active,
.bottom-sheet-leave-active {
  transition: opacity var(--mz-dur) var(--mz-ease);
}

.bottom-sheet-enter-active .bottom-sheet__panel,
.bottom-sheet-leave-active .bottom-sheet__panel {
  transition: transform var(--mz-dur) var(--mz-ease);
}

.bottom-sheet-enter,
.bottom-sheet-leave-to {
  opacity: 0;
}

.bottom-sheet-enter .bottom-sheet__panel,
.bottom-sheet-leave-to .bottom-sheet__panel {
  transform: translateY(100%);
}

@media (prefers-reduced-motion: reduce) {
  .bottom-sheet-enter-active,
  .bottom-sheet-leave-active,
  .bottom-sheet-enter-active .bottom-sheet__panel,
  .bottom-sheet-leave-active .bottom-sheet__panel {
    transition: none;
  }
}
</style>
